/**
 * The company-wide tax setup exception report (GET /api/employees/tax-setup-exceptions, employees.read): which active employees have something to look at,
 * and why. It carries the employee NUMBER only (never a name, address or amount). It is a to-do list for people, not a payroll gate: nothing here blocks
 * payroll, and an employee that is not listed is not thereby "ready".
 */

export interface TaxSetupExceptionCount {
  /** The server's reason code. A code this build does not know is kept and shown with a neutral label rather than dropped. */
  reason: string;
  count: number;
}

export interface TaxSetupExceptionItem {
  employeeId: string;
  employeeNumber: string;
  reasons: string[];
}

export interface TaxSetupExceptions {
  summary: TaxSetupExceptionCount[];
  items: TaxSetupExceptionItem[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface TaxSetupExceptionQuery {
  page?: number;
  pageSize?: number;
  /** Only the employees who have this reason. The summary counts still cover the whole company. */
  reason?: string | undefined;
}

/** Which Employee Setup section (or the Taxes & Exemptions step) fixes a reason. The section keys are the ones Employee Setup deep-links (`?section=`). */
export type ExceptionDestination = { step: 'employee-setup'; section: 'location' | 'address' } | { step: 'taxes-exemptions' };

export interface ExceptionReasonInfo {
  label: string;
  /** One plain sentence: what is the matter, and what it does or does not affect. */
  meaning: string;
  /** What the person looking at the list should do. */
  next: string;
  destination: ExceptionDestination;
}

/** Plain-language text for every reason the server reports, in the server's report order. */
export const EXCEPTION_REASONS: Record<string, ExceptionReasonInfo> = {
  StateDraftNotEffective: {
    label: 'State withholding is only a draft',
    meaning: 'The employee saved state withholding answers, but EPN does not use them for payroll yet.',
    next: 'Review the saved answers in Tax Settings. Nothing changes for payroll until EPN supports effective-dated elections.',
    destination: { step: 'taxes-exemptions' },
  },
  ReciprocityApprovedNoExpiryRecorded: {
    label: 'Reciprocity approved with no expiry',
    meaning: 'A reciprocity certificate was approved before expiry dates were recorded, so there is no date to renew by.',
    next: 'Open the reciprocity review and record an expiry date when the certificate has one.',
    destination: { step: 'taxes-exemptions' },
  },
  ReciprocityStale: {
    label: 'Reciprocity no longer matches the states',
    meaning: 'The approved certificate is for a different work and home state than the employee has now.',
    next: 'Ask the employee for a new certificate if the exemption still applies.',
    destination: { step: 'taxes-exemptions' },
  },
  WorkStateUnknown: {
    label: 'No primary work location',
    meaning: 'EPN cannot tell which state the employee works in, so several checklist items stay open.',
    next: 'Assign a primary work location in Employee Setup.',
    destination: { step: 'employee-setup', section: 'location' },
  },
  ResidenceStateUnknown: {
    label: 'No home address with a state',
    meaning: 'EPN cannot tell which state the employee lives in.',
    next: 'Enter the home address in Employee Setup.',
    destination: { step: 'employee-setup', section: 'address' },
  },
  TerritoryJurisdiction: {
    label: 'Works or lives in a territory',
    meaning: 'EPN does not support payroll tax calculation for U.S. territories yet.',
    next: 'No action here. A payroll tax reviewer has to confirm how this employee is handled.',
    destination: { step: 'taxes-exemptions' },
  },
  MultiStateNeedsReview: {
    label: 'More than one state involved',
    meaning: 'The employee works in or lives in more than one state, and EPN does not allocate wages between states yet.',
    next: 'A payroll tax reviewer has to confirm the situation.',
    destination: { step: 'taxes-exemptions' },
  },
  EmployerSetupUnverified: {
    label: 'Company tax registration not verified',
    meaning: 'EPN could not confirm the company\'s unemployment registration for the work state.',
    next: 'Check the company\'s Tax Payment Settings for that state.',
    destination: { step: 'taxes-exemptions' },
  },  StateElectionPendingApproval: {
    label: 'State withholding election waiting for review',
    meaning: 'A state withholding election was submitted and a reviewer has not approved it yet, so it is not used for payroll.',
    next: 'Ask a reviewer other than the person who entered it to approve or reject it in Tax Settings.',
    destination: { step: 'taxes-exemptions' },
  },
  FederalW4NotInForce: {
    label: 'No federal W-4 in force',
    meaning: 'The employee has no federal W-4 in force today, so federal withholding cannot be calculated.',
    next: 'Ask the employee to complete a federal W-4, or enter it in Tax Settings.',
    destination: { step: 'taxes-exemptions' },
  },
  StateElectionMissing: {
    label: 'State withholding election needed',
    meaning: 'The employee\'s work or home state needs a state withholding certificate, and no approved election is in effect.',
    next: 'Ask the employee to submit their state withholding election, or enter it in Tax Settings for review.',
    destination: { step: 'taxes-exemptions' },
  },
};

const UNKNOWN_REASON: ExceptionReasonInfo = {
  label: 'Needs review',
  meaning: 'EPN flagged this employee for a reason this version of the app does not describe.',
  next: 'Open the employee\'s Tax Settings.',
  destination: { step: 'taxes-exemptions' },
};

export const reasonInfo = (reason: string): ExceptionReasonInfo => EXCEPTION_REASONS[reason] ?? UNKNOWN_REASON;

/** The in-app path for an exception destination. Employee Setup deep-links a sub-section with `?section=` (EPN-94). */
export function destinationPath(employeeId: string, destination: ExceptionDestination): string {
  return destination.step === 'employee-setup' ? `/people/${employeeId}/employee-setup?section=${destination.section}` : `/people/${employeeId}/taxes-exemptions`;
}

type Raw = Record<string, unknown>;
const isRecord = (v: unknown): v is Raw => typeof v === 'object' && v !== null && !Array.isArray(v);
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const text = (v: unknown): string => (typeof v === 'string' ? v : '');
const whole = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

/** Tolerant mapper: a member the server adds later is ignored; an entry without an employee id is dropped (it could not be opened). Null when the body is not an object. */
export function mapTaxSetupExceptions(raw: unknown): TaxSetupExceptions | null {
  if (!isRecord(raw)) return null;
  const summary = list(raw.summary)
    .filter(isRecord)
    .map((s) => ({ reason: text(s.reason), count: whole(s.count, 0) }))
    .filter((s) => s.reason !== '');
  const items = list(raw.items)
    .filter(isRecord)
    .map((i) => ({
      employeeId: text(i.employeeId),
      employeeNumber: text(i.employeeNumber),
      reasons: list(i.reasons).filter((r): r is string => typeof r === 'string'),
    }))
    .filter((i) => i.employeeId !== '');
  return { summary, items, page: whole(raw.page, 1), pageSize: whole(raw.pageSize, 25), totalCount: whole(raw.totalCount, items.length) };
}
